import { AxiosResponse } from "axios";
import { trackPromise } from "react-promise-tracker";

import {
  MaterialStatusCellTrace,
  MaterialStatusGridResponse,
  MaterialStatusMaterialOnHandDetail,
  MaterialStatusSnapshotDto,
  MaterialStatusSummary,
} from "../models/general/material-status";
import Api from "./api-config";
import { BaseService } from "./base-service";
import { URLS } from "./app-urls";

const analysisService = new BaseService<MaterialStatusSummary>(URLS.ANALYSIS);
const materialStatusService = new BaseService<MaterialStatusSnapshotDto>(URLS.MATERIAL_STATUS);
const FALLBACK_EXPORT_NAME = "material-status.xlsx";
export type MaterialStatusExportState = "WAITING" | "STARTED" | "FINISHED" | "ERROR" | "EXPIRED";
export interface MaterialStatusExportJob {
  id: number;
  generation: number;
  status: MaterialStatusExportState;
  task_id: string | null;
  logical_name: string | null;
  error_code: string | null;
  error_message: string | null;
  status_url: string;
  download_url: string | null;
}

export function materialStatusExportFileName(contentDisposition?: string): string {
  if (!contentDisposition) return FALLBACK_EXPORT_NAME;

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  const simpleMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  let candidate = utf8Match?.[1] || simpleMatch?.[1] || simpleMatch?.[2] || "";
  candidate = candidate.trim();
  try {
    candidate = decodeURIComponent(candidate);
  } catch {
    return FALLBACK_EXPORT_NAME;
  }
  if (
    !candidate ||
    candidate.includes("/") ||
    candidate.includes("\\") ||
    !candidate.toLocaleLowerCase().endsWith(".xlsx")
  ) {
    return FALLBACK_EXPORT_NAME;
  }
  return candidate;
}

export async function startMaterialStatusExport(generationUuid: string): Promise<MaterialStatusExportJob> {
  const response = await Api.post<MaterialStatusExportJob>(
    `${URLS.MATERIAL_STATUS}${generationUuid}/export/`,
    {},
    materialStatusService.getOptions(),
  );
  return response.data;
}

export async function getMaterialStatusExport(exportId: number, signal?: AbortSignal): Promise<MaterialStatusExportJob> {
  const response = await Api.get<MaterialStatusExportJob>(
    `${URLS.MATERIAL_STATUS_EXPORTS}${exportId}/`,
    { ...materialStatusService.getOptions(), signal },
  );
  return response.data;
}

export async function waitForMaterialStatusExport(
  initial: MaterialStatusExportJob,
  signal?: AbortSignal,
  intervalMs = 1000,
  maxAttempts = 120,
  onUpdate?: (job: MaterialStatusExportJob) => void,
): Promise<MaterialStatusExportJob> {
  let job = initial;
  onUpdate?.(job);
  for (let attempt = 0; attempt < maxAttempts && ["WAITING", "STARTED"].includes(job.status); attempt += 1) {
    await new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      const onAbort = () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      };
      const timer = window.setTimeout(() => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }, intervalMs);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
    job = await getMaterialStatusExport(job.id, signal);
    onUpdate?.(job);
  }
  if (["WAITING", "STARTED"].includes(job.status)) throw new Error("EXPORT_TIMEOUT");
  return job;
}

export async function downloadMaterialStatusExcel(exportId: number): Promise<string> {
  const response = await Api.get<Blob>(
    `${URLS.MATERIAL_STATUS_EXPORTS}${exportId}/download/`,
    materialStatusService.getOptions("blob"),
  );
  const fileName = materialStatusExportFileName(response.headers?.["content-disposition"]);
  const objectUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  try {
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }
  return fileName;
}

export async function getMaterialStatusSummary(
  analysisId: number | string,
): Promise<AxiosResponse<MaterialStatusSummary>> {
  return await trackPromise(
    Api.get<MaterialStatusSummary>(
      `${URLS.ANALYSIS}${analysisId}/material-status/summary/`,
      analysisService.getOptions(),
    ),
  );
}

export async function generateMaterialStatus(
  analysisId: number | string,
): Promise<AxiosResponse<MaterialStatusSnapshotDto>> {
  return await trackPromise(
    Api.post<MaterialStatusSnapshotDto>(
      `${URLS.ANALYSIS}${analysisId}/material-status/`,
      {},
      analysisService.getOptions(),
    ),
  );
}

/** Returns the grid calculated by the backend, or null for the functional "not generated" (404) state. */
export async function getAnalysisMaterialStatusGrid(
  analysisId: number | string,
  signal?: AbortSignal,
): Promise<MaterialStatusGridResponse | null> {
  try {
    const response = await Api.get<MaterialStatusGridResponse>(
      `${URLS.ANALYSIS}${analysisId}/material-status/grid/`,
      { ...analysisService.getOptions(), signal },
    );
    return response.data;
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
    throw error;
  }
}

/** Loads the calculation memory of one part number and week of the grid. */
export async function getMaterialStatusCellTrace(
  analysisId: number | string,
  partNumber: string,
  week: string,
  signal?: AbortSignal,
): Promise<MaterialStatusCellTrace> {
  const options = analysisService.getOptions();
  const response = await Api.get<MaterialStatusCellTrace>(
    `${URLS.ANALYSIS}${analysisId}/material-status/grid/trace/`,
    { headers: options.headers, params: { part_number: partNumber, week }, signal },
  );
  return response.data;
}

/** Loads the Material On Hands shortage breakdown of one part number and week. */
export async function getMaterialStatusMaterialOnHand(
  analysisId: number | string,
  partNumber: string,
  week: string,
  signal?: AbortSignal,
): Promise<MaterialStatusMaterialOnHandDetail> {
  const options = analysisService.getOptions();
  const response = await Api.get<MaterialStatusMaterialOnHandDetail>(
    `${URLS.ANALYSIS}${analysisId}/material-status/grid/material-on-hand/`,
    { headers: options.headers, params: { part_number: partNumber, week }, signal },
  );
  return response.data;
}

